import { AlertTriangle, Pencil } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  Tabs,
  useToast,
} from '@/components/ui';
import {
  useAddAttendance,
  useCorrectAttendance,
  useManagerAttendance,
  type AttendanceRecord,
} from '@/features/attendance/api';
import { useBranch } from '@/features/branch/branch-context';
import { useSession } from '@/features/auth/session-context';
import { useLocumPeople } from '@/features/locum/api';
import { isApiError } from '@/lib/api';
import { PageContainer } from '@/layouts/PageContainer';
import { useOptionalUrlState, useUrlState } from '@/lib/url-state';
import styles from './attendance.module.css';
import { CLINIC_TZ } from '@/lib/clinic-time';

type Tab = 'today' | 'history' | 'missing';

const KUALA_LUMPUR_TIME_ZONE = CLINIC_TZ;

function toLocalInput(value: string | null) {
  if (!value) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: KUALA_LUMPUR_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(value));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
}

/** datetime-local has no timezone; attendance corrections always use clinic time. */
function toClinicIso(value: string) {
  const [date, time] = value.split('T');
  const seconds = time.length === 5 ? ':00' : '';
  return `${date}T${time}${seconds}+08:00`;
}

function displayDate(value: string) {
  return new Intl.DateTimeFormat('en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
}

function displayTime(value: string | null) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-MY', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value));
}

function localDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function displayTimestampRange(
  clockedInAt: string | null,
  clockedOutAt: string | null,
) {
  return `${displayTime(clockedInAt)}–${displayTime(clockedOutAt)}`;
}

export function ManagerAttendancePage() {
  const { t } = useTranslation();
  const { branches } = useBranch();
  const [linkedRecordId, setLinkedRecordId] = useOptionalUrlState('recordId');
  const [tab, setTab] = useUrlState<Tab>(
    'tab',
    linkedRecordId ? 'history' : 'today',
    ['today', 'history', 'missing'],
  );
  const [branchId, setBranchId] = useUrlState('branch', '');
  const attendance = useManagerAttendance({ branchId });
  const [editingId, setEditingId] = useOptionalUrlState('drawer');
  const editing =
    attendance.data?.find((record) => record.id === editingId) ?? null;
  const linkedRecord = linkedRecordId
    ? (attendance.data?.find((record) => record.id === linkedRecordId) ?? null)
    : null;
  const editingRecord = editing ?? linkedRecord;
  const missing =
    attendance.data?.filter((record) => record.missingClockOut).length ?? 0;
  const branchName = (id: string) =>
    branches.find((branch) => branch.id === id)?.name ?? id;
  const today = localDate(new Date());
  const visibleRecords = attendance.data?.filter((record) => {
    if (tab === 'today') return record.date === today;
    if (tab === 'missing') return record.missingClockOut;
    return record.date !== today;
  });

  const { hasPermission } = useSession();
  const [adding, setAdding] = useState(false);
  // RL-1804: who a missing day can be added for: people seen in the last
  // 60 days of attendance, and the locums (the manager has no staff list).
  const locums = useLocumPeople(hasPermission('locums:read'));
  const people = [
    ...new Map(
      [
        ...(attendance.data ?? []).map((r) => ({
          id: r.staffUserId,
          name: r.staffName ?? r.staffUserId,
        })),
        ...(locums.data ?? []).map((p) => ({ id: p.id, name: p.fullName })),
      ].map((p) => [p.id, p]),
    ).values(),
  ].sort((x, y) => x.name.localeCompare(y.name));

  const closeCorrection = () => {
    setEditingId(null);
    if (linkedRecordId) setLinkedRecordId(null);
  };

  const attendanceColumns = [
    {
      key: 'staff',
      header: t('attendance.staff'),
      render: (record: AttendanceRecord) =>
        record.staffName ?? record.staffUserId,
    },
    {
      key: 'date',
      header: t('attendance.date'),
      render: (record: AttendanceRecord) => displayDate(record.date),
    },
    {
      key: 'branch',
      header: t('attendance.branch'),
      render: (record: AttendanceRecord) => branchName(record.branchId),
    },
    {
      key: 'in',
      header: t('attendance.clockIn'),
      render: (record: AttendanceRecord) => displayTime(record.clockedInAt),
    },
    {
      key: 'out',
      header: t('attendance.clockOut'),
      render: (record: AttendanceRecord) => displayTime(record.clockedOutAt),
    },
    {
      key: 'flags',
      header: t('attendance.status'),
      render: (record: AttendanceRecord) => (
        <div className={styles.badges}>
          {record.missingClockOut ? (
            <Badge tone="danger">{t('attendance.missingClockOut')}</Badge>
          ) : (
            <Badge tone="success">
              {t(`attendance.states.${record.state}`)}
            </Badge>
          )}
          {record.late && <Badge tone="warning">{t('attendance.late')}</Badge>}
          {record.earlyDeparture && (
            <Badge tone="warning">{t('attendance.early')}</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'correction',
      header: t('attendance.correctionProvenance'),
      render: (record: AttendanceRecord) =>
        record.correctedTimestamps ? (
          <div className={styles.provenance}>
            <span>
              {t('attendance.originalTimestamp', {
                value: displayTimestampRange(
                  record.originalTimestamps.clockedInAt,
                  record.originalTimestamps.clockedOutAt,
                ),
              })}
            </span>
            <strong>
              {t('attendance.correctedTimestamp', {
                value: displayTimestampRange(
                  record.correctedTimestamps.clockedInAt,
                  record.correctedTimestamps.clockedOutAt,
                ),
              })}
            </strong>
          </div>
        ) : (
          '—'
        ),
    },
    {
      key: 'actions',
      header: t('attendance.actions'),
      render: (record: AttendanceRecord) => (
        <Button
          size="sm"
          icon={<Pencil size={14} />}
          onClick={() => setEditingId(record.id)}
        >
          {record.missingClockOut
            ? t('attendance.resolve')
            : t('attendance.correct')}
        </Button>
      ),
    },
  ];

  return (
    <PageContainer>
      <div className={styles.stack}>
        <Tabs
          variant="bar"
          label={t('attendance.managerTitle')}
          value={tab}
          onChange={setTab}
          items={[
            { value: 'today', label: t('attendance.todayTab') },
            { value: 'history', label: t('attendance.historyTab') },
            {
              value: 'missing',
              label: t('attendance.missingTab'),
              count: missing,
            },
          ]}
        />

        {tab === 'missing' && missing > 0 && (
          <div className={styles.missingNotice} role="status">
            <AlertTriangle size={18} />
            {t('attendance.missingSummary', { count: missing })}
          </div>
        )}
        <Card
          title={t(`attendance.${tab}Tab`)}
          actions={
            <span className={styles.actions}>
              <Button size="sm" onClick={() => setAdding(true)}>
                {t('attendance.addRecord')}
              </Button>
              <Select
                aria-label={t('attendance.branch')}
                value={branchId}
                onChange={(event) => setBranchId(event.target.value)}
                placeholder={t('attendance.allBranches')}
                options={branches.map((branch) => ({
                  value: branch.id,
                  label: branch.name,
                }))}
              />
            </span>
          }
          flush
        >
          <DataTable
            caption={t(`attendance.${tab}Tab`)}
            rows={visibleRecords}
            rowKey={(record) => record.id}
            loading={attendance.isLoading}
            error={attendance.error}
            onRetry={() => void attendance.refetch()}
            empty={<EmptyState title={t('attendance.noHistory')} />}
            columns={attendanceColumns}
          />
        </Card>
      </div>
      <CorrectionDialog record={editingRecord} onClose={closeCorrection} />
      {adding && (
        <AddRecordDialog people={people} onClose={() => setAdding(false)} />
      )}
    </PageContainer>
  );
}

function CorrectionDialog({
  record,
  onClose,
}: {
  record: AttendanceRecord | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const correction = useCorrectAttendance();
  const toast = useToast();
  const [clockedInAt, setClockedInAt] = useState('');
  const [clockedOutAt, setClockedOutAt] = useState('');
  const [reason, setReason] = useState('');
  const [attempted, setAttempted] = useState(false);
  const clockedOutError =
    isApiError(correction.error) && correction.error.status === 400
      ? correction.error.fieldError('clockedOutAt')
      : undefined;

  const close = () => {
    correction.reset();
    setAttempted(false);
    setClockedInAt('');
    setClockedOutAt('');
    setReason('');
    onClose();
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const input = clockedInAt || toLocalInput(record?.clockedInAt ?? null);
    const output = clockedOutAt || toLocalInput(record?.clockedOutAt ?? null);
    setAttempted(true);
    if (!record || !input || !reason.trim()) return;
    correction.mutate(
      {
        id: record.id,
        clockedInAt: toClinicIso(input),
        clockedOutAt: output ? toClinicIso(output) : null,
        reason: reason.trim(),
      },
      {
        onSuccess: () => {
          toast.show({ tone: 'success', title: t('attendance.corrected') });
          close();
        },
      },
    );
  };

  return (
    <Dialog
      open={record !== null}
      onClose={close}
      title={
        record?.missingClockOut
          ? t('attendance.resolveMissing')
          : t('attendance.correctTitle')
      }
      footer={
        <>
          <Button onClick={close}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            type="submit"
            form="attendance-correction"
            loading={correction.isPending}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form
        id="attendance-correction"
        className={styles.correctionForm}
        onSubmit={submit}
      >
        <Field
          label={t('attendance.clockIn')}
          error={
            attempted && !(clockedInAt || record?.clockedInAt)
              ? t('attendance.timeRequired')
              : undefined
          }
        >
          <Input
            type="datetime-local"
            value={clockedInAt || toLocalInput(record?.clockedInAt ?? null)}
            onChange={(event) => setClockedInAt(event.target.value)}
          />
        </Field>
        <Field
          label={t('attendance.clockOut')}
          hint={t('attendance.clockOutHint')}
          error={clockedOutError}
        >
          <Input
            type="datetime-local"
            value={clockedOutAt || toLocalInput(record?.clockedOutAt ?? null)}
            onChange={(event) => setClockedOutAt(event.target.value)}
          />
        </Field>
        <Field
          label={t('attendance.correctionReason')}
          error={
            attempted && !reason.trim()
              ? t('attendance.reasonRequired')
              : undefined
          }
        >
          <textarea
            className={styles.textarea}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
        {correction.error && <ErrorState error={correction.error} />}
      </form>
    </Dialog>
  );
}

/**
 * RL-1804 (gap #33): add a day the person did not clock (locum pay counts
 * these records). A past day in clinic time; the API refuses days inside an
 * approved or paid pay week and days that already have a record.
 */
function AddRecordDialog({
  people,
  onClose,
}: {
  people: { id: string; name: string }[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { branches } = useBranch();
  const add = useAddAttendance();
  const toast = useToast();
  const [staffUserId, setStaffUserId] = useState('');
  const [branchId, setBranchId] = useState(
    branches.length === 1 ? branches[0].id : '',
  );
  const [date, setDate] = useState('');
  const [inTime, setInTime] = useState('');
  const [outTime, setOutTime] = useState('');
  const [reason, setReason] = useState('');
  const [attempted, setAttempted] = useState(false);
  // Review fix: the clinic's (Malaysia) yesterday, whatever the browser's zone;
  // today is clocked by the person, never added.
  const [lastDay] = useState(() =>
    new Date(Date.now() + 8 * 3_600_000 - 86_400_000)
      .toISOString()
      .slice(0, 10),
  );
  const missing =
    !staffUserId || !branchId || !date || !inTime || !outTime || !reason.trim();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (missing) return;
    add.mutate(
      {
        staffUserId,
        branchId,
        clockedInAt: toClinicIso(`${date}T${inTime}`),
        clockedOutAt: toClinicIso(`${date}T${outTime}`),
        reason: reason.trim(),
      },
      {
        onSuccess: () => {
          toast.show({ tone: 'success', title: t('attendance.recordAdded') });
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('attendance.addRecord')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            type="submit"
            form="attendance-add"
            loading={add.isPending}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form
        id="attendance-add"
        className={styles.correctionForm}
        onSubmit={submit}
      >
        <p className={styles.hint}>{t('attendance.addRecordHint')}</p>
        <Field label={t('attendance.staff')}>
          <Select
            value={staffUserId}
            placeholder={t('attendance.choosePerson')}
            options={people.map((p) => ({ value: p.id, label: p.name }))}
            onChange={(event) => setStaffUserId(event.target.value)}
          />
        </Field>
        <Field label={t('attendance.branch')}>
          <Select
            value={branchId}
            placeholder={t('attendance.chooseBranch')}
            options={branches.map((b) => ({ value: b.id, label: b.name }))}
            onChange={(event) => setBranchId(event.target.value)}
          />
        </Field>
        <Field label={t('attendance.date')}>
          <Input
            type="date"
            max={lastDay}
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </Field>
        <Field label={t('attendance.clockIn')}>
          <Input
            type="time"
            value={inTime}
            onChange={(event) => setInTime(event.target.value)}
          />
        </Field>
        <Field label={t('attendance.clockOut')}>
          <Input
            type="time"
            value={outTime}
            onChange={(event) => setOutTime(event.target.value)}
          />
        </Field>
        <Field
          label={t('attendance.correctionReason')}
          error={
            attempted && missing ? t('attendance.addRecordRequired') : undefined
          }
        >
          <textarea
            className={styles.textarea}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
        {add.error && <ErrorState error={add.error} />}
      </form>
    </Dialog>
  );
}
